/**
 * AuthContext.js — P0-2 hardened version
 *
 * Tokens are stored exclusively in httpOnly cookies set by the server.
 * The frontend NEVER stores tokens in localStorage or reads them from JS.
 * All API calls use `withCredentials: true` so cookies are sent automatically.
 * `getAuthHeaders()` returns {} — auth is handled by the cookie, not the header.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { setSentryUser } from '../config/sentry';
import { logError } from '../utils/logger';
import { csrfHeaders } from '../utils/csrf';
import { claimPendingReferral } from '../utils/referral';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Clear any legacy localStorage tokens from before P0-2
// This runs once on import to ensure clean migration for existing users.
localStorage.removeItem('token');
localStorage.removeItem('refresh_token');
localStorage.removeItem('access_token');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);

  // Update Sentry user context when user changes
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  // Silently refresh the session using the httpOnly refresh_token cookie.
  // The server reads the cookie directly — no token in the request body.
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API}/auth/refresh`,
        {},                         // empty body — token is in the cookie
        { withCredentials: true }
      );
      const { expires_in } = response.data;

      // Schedule the next silent refresh (5 min before expiry)
      const refreshMs = Math.max((expires_in - 300) * 1000, 60_000);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshMs);

      return true;
    } catch {
      setUser(null);
      return false;
    }
  }, []);

  // On mount: ask the server whether the session cookie is valid.
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        // Cookie expired — try silent refresh first
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          try {
            const retry = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(retry.data);
            return;
          } catch (retryError) {
            logError(retryError, { context: 'AuthContext.authCheck.retry' });
          }
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    // CRITICAL: If returning from Emergent Google OAuth, skip the /me check.
    // AuthCallback exchanges the session_id and establishes the session first;
    // running checkAuth here would race and wipe the freshly signed-in user.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
    } else {
      checkAuth();
    }
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [checkAuth]);

  // Apply a referral code saved on the sign-up page once the new account is signed in
  useEffect(() => {
    if (user?.user_id) claimPendingReferral();
  }, [user?.user_id]);

  const login = async (email, password) => {
    // Server sets httpOnly cookies; response body contains only user + expires_in
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // Admins must finish a second step (emailed code) before a session exists
    if (response.data.requires_2fa) return response.data;
    return startSession(response.data);
  };

  const startSession = ({ user: userData, expires_in }) => {
    setUser(userData);
    const refreshMs = Math.max((expires_in - 300) * 1000, 60_000);
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshMs);
    return userData;
  };

  const verifyAdminCode = async (challengeId, code, trustDevice) => {
    const response = await axios.post(
      `${API}/auth/admin-2fa/verify`,
      { challenge_id: challengeId, code, trust_device: trustDevice },
      { withCredentials: true }
    );
    return startSession(response.data);
  };

  const resendAdminCode = async (challengeId) => {
    const response = await axios.post(`${API}/auth/admin-2fa/resend`, { challenge_id: challengeId }, { withCredentials: true });
    return response.data;
  };

  const register = async (email, password, name, businessType, filingStatus = 'single', extra = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      business_type: businessType,
      filing_status: filingStatus,
      tos_accepted:  extra.tos_accepted ?? false,   // D1: forward ToS flag
    }, { withCredentials: true });
    return response.data;
  };

  const handleGoogleSession = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    const { user: userData, expires_in } = response.data;
    setUser(userData);

    if (expires_in) {
      const refreshMs = Math.max((expires_in - 300) * 1000, 60_000);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshMs);
    }

    return userData;
  };

  const logout = async () => {
    try {
      // Server clears both httpOnly cookies with Max-Age=0
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logError(error, { context: 'AuthContext.logout' });
    }
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    setUser(null);
  };

  // P0-2: No token in localStorage → no Authorization header.
  // All requests send the httpOnly cookie automatically via withCredentials: true.
  const getAuthHeaders = () => csrfHeaders();

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      verifyAdminCode,
      resendAdminCode,
      register,
      logout,
      handleGoogleSession,
      getAuthHeaders,
      refreshAccessToken,
      refreshUser: checkAuth,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
